
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Search,
  Download,
  Upload,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  AlertCircle,
  Trash2,
  RotateCcw,
  MoreVertical,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { isSuperAdmin } from 'utlis/authUtils';
import SearchInput from './SearchInput';

/* ------------------------------------------------------------------ */
/*  CENTRAL DataTable – export + import + preview (all rows, all fields) */
/* ------------------------------------------------------------------ */
const DataTable = ({
  data = [],
  columns = [],
  onExport,
  onImportPreview,
  onImportConfirm,
  showSearch = true,
  showExport = true,
  showImport = true,
  showSerialNo = true,
  searchPlaceholder = 'Search...',
  pageIndex = 0,
  pageSize = 10,
  pageCount = -1,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  className,
  disablePagination = false,
  // NEW PROPS for handling soft deletes
  hasSoftDelete = true, // Flag to indicate if the data supports soft delete
  onDelete, // Callback for delete action
  onRestore, // Callback for restore action
  totalCount = 0,
  templateUrl,
  templateDownloadName = 'import-template.csv',
  templateKey,
}) => {
  /* -------------------------- STATE -------------------------- */
  const [sorting, setSorting] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  // Import
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Preview pagination (client-side, 10 per page)
  const [previewPageIndex, setPreviewPageIndex] = useState(0);
  const previewPageSize = 10;

  // NEW: State for delete/restore confirmations
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [itemToAction, setItemToAction] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  /* -------------------------- CURSOR PRESERVATION -------------------------- */
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return;
    const saved = input.dataset.savedPos
      ? Number(input.dataset.savedPos)
      : input.value.length;
    input.setSelectionRange(saved, saved);
    input.focus();
  }, [data]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const pos = e.target.selectionStart ?? 0;
    if (searchInputRef.current) {
      searchInputRef.current.dataset.savedPos = pos.toString();
    }
    setSearchTerm(value);
    onSearch?.(value);
  };

  /* -------------------------- FINAL FIX: Reset to Page 1 only on actual search change -------------------------- */
  const prevSearchTermRef = useRef('');

  useEffect(() => {
    // Only reset to page 1 if the search term actually changed (and it's not empty)
    if (searchTerm !== prevSearchTermRef.current && searchTerm.trim() !== '') {
      if (pageIndex !== 0) {
        onPageChange?.(0);
      }
    }
    // Update the ref with current search term
    prevSearchTermRef.current = searchTerm;
  }, [searchTerm, pageIndex, onPageChange]);
  /* -------------------------- TABLE DATA -------------------------- */
  const tableData = importPreview ? importPreview.preview : data;

  /* -------------------------- SERIAL NUMBER COLUMN -------------------------- */
  const serialNoColumn = useMemo(() => {
    if (!showSerialNo) return null;

    return {
      id: 'serialNo',
      header: 'S.No.',
      cell: ({ row }) => {
        // FIXED: Continuous numbering in preview mode
        if (importPreview) {
          // In preview: use global row index (not page-based)
          return row.index + 1;
        }

        // Normal mode: use server pagination (pageIndex + pageSize)
        const currentPageIndex = pageIndex;
        const currentPageSize = pageSize;
        return currentPageIndex * currentPageSize + row.index + 1;
      },
      size: 60,
    };
  }, [showSerialNo, pageIndex, pageSize, importPreview]);

  /* -------------------------- DYNAMIC COLUMNS -------------------------- */
  // 1. Normal columns (exclude the built-in "actions" column)
  const baseColumns = columns.filter((c) => c.id !== 'actions');

  // 2. When in preview → create a column for **every key** that exists in the CSV
  const previewDynamicColumns = useMemo(() => {
    if (!importPreview || tableData.length === 0) return [];
    const firstRow = tableData[0];
    const csvKeys = Object.keys(firstRow).filter(key => key !== '__resolved');

    return csvKeys.map((key) => ({
      id: key, // ← Critical when using function header
      accessorKey: key,
      header: () => (
        <span className="capitalize font-medium">
          {key.replace(/_/g, ' ')}
        </span>
      ),
      cell: ({ row }) => {
        const val = row.original[key];
        return val === '' || val == null ? <span className="text-muted-foreground">—</span> : String(val);
      },
    }));
  }, [importPreview, tableData]);

  /* -------------------------- ERROR MAP -------------------------- */
  const errorMap = useMemo(() => {
    const map = new Map();
    if (importPreview?.errors) {
      importPreview.errors.forEach((errorObj) => {
        const key = errorObj.row.code ?? JSON.stringify(errorObj.row);
        map.set(key, errorObj.errors);
      });
    }
    return map;
  }, [importPreview?.errors]);

  // NEW: Enhanced actions column with delete/restore functionality
  const enhancedActionsColumn = useMemo(() => {
    const originalActionsColumn = columns.find((c) => c.id === 'actions');

    return {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const isDeleted = row.original.is_deleted;


        // 🔹 CASE 1: NOT DELETED → show original actions directly
        if (!isDeleted) {
          return originalActionsColumn?.cell
            ? originalActionsColumn.cell({ row })
            : null;
        }

        // 🔹 CASE 2: DELETED → show only Delete + Restore
        return (
          <DropdownMenu>
            {isSuperAdmin() &&
              <>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      setItemToAction(row.original);
                      setRestoreConfirmOpen(true);
                    }}
                    className="cursor-pointer text-green-600 flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" /> Restore
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => {
                      setItemToAction(row.original);
                      setDeleteConfirmOpen(true);
                    }}
                    className="cursor-pointer text-destructive flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" /> Permanent Delete
                  </DropdownMenuItem>


                </DropdownMenuContent>
              </>
            }
          </DropdownMenu>
        );
      },
    };
  }, [columns, hasSoftDelete]);


  // 3. Final columns
  const finalColumns = useMemo(() => {
    const columnsWithSerial = showSerialNo && serialNoColumn ? [serialNoColumn] : [];

    if (importPreview) {
      return [
        ...columnsWithSerial,
        ...previewDynamicColumns,
        {
          id: 'importErrors',
          header: 'Errors',
          cell: ({ row }) => {
            const key = row.original.code ?? JSON.stringify(row.original);
            const errs = errorMap.get(key);
            if (!errs) return '—';
            return (
              <ul className="list-disc list-inside text-xs space-y-1 text-destructive">
                {Object.entries(errs).map(([field, msgs]) => (
                  <li key={field}>
                    <strong>{field}:</strong>{' '}
                    {Array.isArray(msgs) ? msgs.join(', ') : msgs}
                  </li>
                ))}
              </ul>
            );
          },
        },
      ];
    }
    else {
      const hasDeletedData = data.some(item => item.is_deleted === true);

      if (!hasDeletedData) {
        // 🔹 When delete is FALSE → go into IF
        return [
          ...columnsWithSerial,
          ...columns,
        ];
      } else {
        // 🔹 When delete is TRUE → go into ELSE
        const actionsColumn = hasSoftDelete
          ? enhancedActionsColumn
          : columns.find((c) => c.id === 'actions');

        const otherColumns = columns.filter((c) => c.id !== 'actions');

        return [
          ...columnsWithSerial,
          ...columns.filter((c) => c.id !== 'actions'),
          enhancedActionsColumn,
        ];

      }
    }
  }, [showSerialNo, serialNoColumn, importPreview, previewDynamicColumns, columns, errorMap, hasSoftDelete, enhancedActionsColumn]);

  /* -------------------------- TABLE INSTANCE -------------------------- */
  const table = useReactTable({
    data: tableData,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: disablePagination ? undefined : getPaginationRowModel(),
    manualPagination: disablePagination ? false : !importPreview,

    pageCount: importPreview
      ? Math.ceil(tableData.length / previewPageSize)
      : pageCount > -1
        ? pageCount
        : undefined,
    state: {
      sorting,
      ...(disablePagination
        ? {}
        : {
          pagination: importPreview
            ? { pageIndex: previewPageIndex, pageSize: previewPageSize }
            : { pageIndex, pageSize },
        }),
    },

    onSortingChange: setSorting,
    onPaginationChange: disablePagination
      ? undefined
      : (updater) => {
        const newState =
          typeof updater === 'function'
            ? updater(
              importPreview
                ? { pageIndex: previewPageIndex, pageSize: previewPageSize }
                : { pageIndex, pageSize }
            )
            : updater;

        if (importPreview) {
          setPreviewPageIndex(newState.pageIndex);
        } else {
          onPageChange?.(newState.pageIndex);
          onPageSizeChange?.(newState.pageSize);
        }
      },
  });

  /* -------------------------- EXPORT -------------------------- */
  const handleExport = useCallback(() => {
    if (onExport) {
      onExport(data);
      return;
    }

    // Include serial number in export if enabled
    const exportColumns = showSerialNo
      ? [{ header: 'S.No.', accessorKey: 'serialNo' }, ...columns.filter((c) => c.id !== 'actions')]
      : columns.filter((c) => c.id !== 'actions');

    const header = exportColumns
      .map((c) => (typeof c.header === 'function' ? c.header() : c.header) ?? '')
      .join(',');

    const rows = data.map((row, index) => {
      const rowData = [];

      // Add serial number if enabled
      if (showSerialNo) {
        rowData.push(index + 1);
      }

      // Add other column data
      columns
        .filter((c) => c.id !== 'actions')
        .forEach((c) => {
          const val = row[c.accessorKey] ?? '';
          const str = String(val);
          rowData.push(str.includes(',') || str.includes('"')
            ? `"${str.replace(/"/g, '""')}"`
            : str);
        });

      return rowData.join(',');
    });

    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, columns, onExport, showSerialNo]);

  /* -------------------------- IMPORT -------------------------- */
  const triggerFileInput = () => {
    if (templateKey && templateUrl) {
      const today = new Date().toISOString().slice(0, 10);
      const storageKey = `template_downloaded_${templateKey}`;
      const downloadedToday = localStorage.getItem(storageKey) === today;

      if (!downloadedToday) {
        const link = document.createElement('a');
        link.href = templateUrl;                    // ← this works perfectly with /import-templates/...
        link.download = templateDownloadName;
        document.body.appendChild(link);            // ← important in some browsers
        link.click();
        document.body.removeChild(link);
        localStorage.setItem(storageKey, today);
        toast.success('Import template downloaded. Please fill it and upload.');
      }
    }

    document.getElementById('hidden-file-input')?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setShowImportDialog(true);
    setImportPreview(null);
    setPreviewPageIndex(0);
    e.target.value = '';
  };

  const runPreview = async () => {
    if (!importFile || !onImportPreview) return;
    setPreviewing(true);
    try {
      const preview = await onImportPreview(importFile);
      setImportPreview(preview);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to preview import');
    } finally {
      setPreviewing(false);
    }
  };

  const runConfirm = async () => {
    if (!importPreview || !onImportConfirm) return;
    setImporting(true);
    try {
      await onImportConfirm(importPreview.preview);
      toast.success('Import completed successfully');
      setImportPreview(null);
      setImportFile(null);
      setShowImportDialog(false);
      setPreviewPageIndex(0);
    } catch (err) {
      toast.error(err?.message ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const cancelPreview = () => {
    setImportPreview(null);
    setImportFile(null);
    setPreviewPageIndex(0);
    const input = document.getElementById('hidden-file-input');
    if (input) input.value = '';
  };

  /* -------------------------- NEW: Delete/Restore Handlers -------------------------- */
  const handleDelete = async () => {
    if (!itemToAction || !onDelete) return;

    setActionInProgress(true);
    try {
      await onDelete(itemToAction.id);
      toast.success('Item deleted successfully');
      setDeleteConfirmOpen(false);
      setItemToAction(null);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to delete item');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRestore = async () => {
    if (!itemToAction || !onRestore) return;

    setActionInProgress(true);
    try {
      await onRestore(itemToAction.id);
      toast.success('Item restored successfully');
      setRestoreConfirmOpen(false);
      setItemToAction(null);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to restore item');
    } finally {
      setActionInProgress(false);
    }
  };

  /* -------------------------- HELPERS -------------------------- */
  const hasErrors = importPreview?.errors && importPreview.errors.length > 0;
  const canConfirm = !hasErrors && importPreview?.preview?.length;


  /*---------------------No data hide export---------------------*/
  const hasDataToExport = Array.isArray(data) && data.length > 0;


  /*----------------------Pgaintaion for normal mode---------------------*/
  const start = totalCount === 0 ? 0 : (pageIndex * pageSize) + 1;
  const end = Math.min((pageIndex + 1) * pageSize, totalCount);

  /* -------------------------- RENDER -------------------------- */
  return (
    <div className={cn('space-y-4', className)}>
      {/* ==== ACTIONS ==== */}
      {(showSearch || showExport || showImport) && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* SEARCH */}
          {/* {showSearch && !importPreview && (
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10"
                disabled={isLoading}
                autoComplete="off"
              />
            </div>
          )} */}
          {(showSearch && !importPreview) ? (
            <div className="w-full sm:w-80">
              <SearchInput
                value={searchTerm}
                onChange={(value) => {
                  setSearchTerm(value);
                  onSearch?.(value);
                }}
                placeholder={searchPlaceholder}
                label={null}  // Hide label since it's inside table toolbar
                className="mb-0" // Remove extra spacing if needed
              />
            </div>
          ) : (<div />)}

          {/* EXPORT / IMPORT */}
          {(showExport || showImport) && (
            <div className="flex items-center gap-2">
              <TooltipProvider>
                {showExport && !importPreview && hasDataToExport && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExport}
                        disabled={isLoading}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Export
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Export data to CSV</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {showImport && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={triggerFileInput}
                        disabled={isLoading}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Import
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Import data from CSV/Excel</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>

              <input
                id="hidden-file-input"
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          )}
        </div>
      )}

      {/* LOADING OVERLAY */}
      {/* {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span>Loading...</span>
          </div>
        </div>
      )} */}

      {/* PREVIEW ALERT */}
      {importPreview && (
        <Alert variant={hasErrors ? 'destructive' : 'default'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="!translate-y-0">
            {hasErrors
              ? `${importPreview.errors.length} row(s) have validation errors. Fix them before importing.`
              : `Ready to import ${importPreview.total} row${importPreview.total !== 1 ? 's' : ''}.`}
          </AlertDescription>
        </Alert>
      )}

      {/* MAIN / PREVIEW TABLE */}
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <div className="overflow-auto custom-scrollbar">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup?.id} className="hover:bg-transparent">
                  {headerGroup?.headers.map((header) => (
                    <TableHead key={header?.id} className="font-semibold whitespace-nowrap">
                      {header?.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'flex items-center gap-2',
                            header?.column?.getCanSort() &&
                            'cursor-pointer select-none hover:text-foreground'
                          )}
                          onClick={header?.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header?.column?.columnDef.header,
                            header?.getContext()
                          )}
                          {header?.column.getCanSort() && (
                            <span className="text-muted-foreground">
                              {header?.column.getIsSorted() === 'asc' ? (
                                <ArrowUp className="h-4 w-4" />
                              ) : header?.column.getIsSorted() === 'desc' ? (
                                <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  // Check if this row is deleted (only if soft delete is enabled)
                  const isDeleted = hasSoftDelete && row.original.is_deleted;

                  // Critical: Use row.index to match errors by position in preview array
                  const rowKey = row.original.code ?? JSON.stringify(row.original);
                  const hasRowError = errorMap.has(rowKey);

                  return (
                    <TableRow
                      key={row.id}
                      className={cn(
                        'table-row',
                        hasRowError && 'bg-destructive/5',
                        // NEW: Apply reddish styling to deleted rows
                        isDeleted && 'bg-red-100 border-l-4 border-l-red-500'
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            hasRowError && 'text-destructive',
                            'whitespace-nowrap',
                            // NEW: Apply text styling to deleted rows
                            isDeleted && 'text-red-700'
                          )}
                        >
                          {/* NEW: Add deletion indicator to the first cell of deleted rows */}
                          {isDeleted && cell.column.id === 'serialNo' && (
                            // <div className="flex items-center gap-4">
                            //   {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            //   <div className='flex flex-col'>
                            //   <Badge variant="destructive" className="text-xs">Deleted</Badge>

                            // <div className="flex flex-col gap-1">

                            //   {row.original.deleted_by_name && (
                            //     <span className="text-xs text-red-600">
                            //       Deleted by: {row.original.deleted_by_name}
                            //     </span>
                            //   )}
                            //   {row.original.deleted_at && (
                            //     <span className="text-xs text-red-600">
                            //       on: {new Date(row.original.deleted_at).toLocaleDateString()}
                            //     </span>
                            //   )}
                            // </div>
                            // </div>

                            // </div>
                            <div className="flex items-start gap-4">
                              {/* Main cell content */}
                              <div>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>

                              {/* Deleted info */}
                              <div className="flex flex-col gap-1">
                                <Badge variant="destructive" className="w-fit text-xs">
                                  Deleted
                                </Badge>

                                {row.original.deleted_by_name && (
                                  <span className="text-xs text-red-600">
                                    Deleted by: {row.original.deleted_by_name}
                                  </span>
                                )}

                                {row.original.deleted_at && (
                                  <span className="text-xs text-red-600">
                                    On: {new Date(row.original.deleted_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>

                          )}
                          {/* For all other cells, just render normally */}
                          {!isDeleted || cell.column.id !== 'serialNo' ? (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          ) : null}

                          {/* NEW: Show deletion info in a dedicated cell if available */}
                          {/* {isDeleted && cell.column.id === 'name' && (
                            <div className="flex flex-col gap-1">
                           
                              {row.original.deleted_by_name && (
                                <span className="text-xs text-red-600">
                                  Deleted by: {row.original.deleted_by_name}
                                </span>
                              )}
                              {row.original.deleted_at && (
                                <span className="text-xs text-red-600">
                                  on: {new Date(row.original.deleted_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          )} */}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={finalColumns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {importPreview ? 'No rows to preview.' : 'No results found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* PREVIEW PAGINATION (client-side) */}
      {importPreview && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>
            Showing {(previewPageIndex * previewPageSize) + 1} to{' '}
            {Math.min((previewPageIndex + 1) * previewPageSize, tableData.length)} of{' '}
            {tableData.length} entries
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2">
              {previewPageIndex + 1} / {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* PREVIEW ACTION BAR */}
      {importPreview && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={cancelPreview}>
            Choose another file
          </Button>
          <Button
            onClick={runConfirm}
            disabled={!canConfirm || importing}
            variant={hasErrors ? 'secondary' : 'default'}
          >
            {importing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing…
              </>
            ) : (
              `Confirm Import`
            )}
          </Button>
        </div>
      )}

      {/* NORMAL PAGINATION */}
      {!importPreview && !disablePagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* <div className="text-sm text-muted-foreground">
            Showing {data.length === 0 ? 0 : (pageIndex * pageSize) + 1} to{' '}
            {Math.min((pageIndex + 1) * pageSize, data.length)} of {data.length} entries
          </div> */}
          <div className="text-sm text-muted-foreground">
            Showing {start} to {end} of {totalCount} entries
          </div>


          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(v) => onPageSizeChange?.(Number(v))}
                disabled={isLoading}
              >
                <SelectTrigger className="w-16 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 30, 50, 100].map((sz) => (
                    <SelectItem key={sz} value={sz.toString()}>
                      {sz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange?.(0)}
                disabled={!table.getCanPreviousPage() || isLoading}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage() || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-1 px-2">
                <span className="text-sm font-medium">{pageIndex + 1}</span>
                <span className="text-sm text-muted-foreground">/</span>
                <span className="text-sm text-muted-foreground">
                  {pageCount > -1 ? pageCount : '…'}
                </span>
              </div>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage() || isLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange?.(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage() || isLoading}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* IMPORT FILE-PICKER DIALOG */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Data</DialogTitle>
            <DialogDescription>
              {importFile?.name ?? 'Select a file'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-8">
            <Button
              size="lg"
              onClick={async () => {
                await runPreview();
                setShowImportDialog(false);
              }}
              disabled={!importFile || previewing}
            >
              {previewing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating preview…
                </>
              ) : (
                'Preview Import'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this item? This action can be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionInProgress}
            >
              {actionInProgress ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Restore Confirmation Dialog */}
      <Dialog open={restoreConfirmOpen} onOpenChange={setRestoreConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Restoration</DialogTitle>
            <DialogDescription>
              Are you sure you want to restore this item? It willl be restore to it's previous state.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRestoreConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleRestore}
              disabled={actionInProgress}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionInProgress ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restoring...
                </>
              ) : (
                'Restore'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataTable;